import React, { useState } from 'react';
import { X, Database, CheckSquare, Square, Download, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { useI18n } from '../hooks/useI18n';
import { useAuth } from '../context/AuthContext';
import { exportService, ExportProgress } from '../services/exportService';

interface ExportModalProps {
    isOpen: boolean;
    onClose: () => void;
}

type EntityType = 'COMPANY' | 'CLIENT' | 'SUPPLIER' | 'INVOICE' | 'ORDER' | 'PRODUCT' | 'CATEGORY';

interface EntityOption {
    id: EntityType;
    label: string;
}

const ExportModal: React.FC<ExportModalProps> = ({ isOpen, onClose }) => {
    const { t, isRTL } = useI18n();
    const { user } = useAuth();
    const [selectedEntities, setSelectedEntities] = useState<EntityType[]>([]);
    const [isExporting, setIsExporting] = useState(false);
    const [currentEntity, setCurrentEntity] = useState<EntityType | null>(null);
    const [progress, setProgress] = useState<ExportProgress | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [completed, setCompleted] = useState<EntityType[]>([]);

    const entities: EntityOption[] = [
        { id: 'COMPANY', label: t('sidebar.companies') },
        { id: 'CLIENT', label: t('sidebar.clients') },
        { id: 'SUPPLIER', label: t('suppliers.title') },
        { id: 'INVOICE', label: t('sidebar.invoices') },
        { id: 'ORDER', label: t('stock.orders.title') },
        { id: 'PRODUCT', label: t('stock.products.title') },
        { id: 'CATEGORY', label: t('stock.categories.title') },
    ];

    const toggleEntity = (id: EntityType) => {
        if (selectedEntities.includes(id)) {
            setSelectedEntities(selectedEntities.filter(e => e !== id));
        } else {
            setSelectedEntities([...selectedEntities, id]);
        }
    };

    const handleStartExport = async () => {
        if (selectedEntities.length === 0 || !user?.entityId) return;

        setIsExporting(true);
        setError(null);
        setCompleted([]);

        try {
            for (const entity of selectedEntities) {
                setCurrentEntity(entity);
                setProgress(null);

                // Pass empty string for companyId to trigger full entity export
                await exportService.startExport(entity, '', user.entityId, (p) => {
                    setProgress(p);
                });

                setCompleted(prev => [...prev, entity]);
            }

            // All done
            setTimeout(() => {
                setIsExporting(false);
                setCurrentEntity(null);
                setProgress(null);
            }, 1000);
        } catch (err: any) {
            console.error('Bulk export failed:', err);
            setError(err.message || 'Export failed');
            setIsExporting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
            <div className={`bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 rounded-lg">
                            <Database className="w-5 h-5 text-indigo-600" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">{t('sidebar.dataExport')}</h2>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isExporting}
                        className="p-2 hover:bg-gray-200 rounded-full transition-colors disabled:opacity-50"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 flex-1 overflow-y-auto">
                    <p className="text-gray-600 mb-6">
                        {t('export.modalDescription', { defaultValue: 'Select the data you want to export as a backup for your entire entity.' })}
                    </p>

                    <div className="grid grid-cols-1 gap-3">
                        {entities.map((entity) => {
                            const isSelected = selectedEntities.includes(entity.id);
                            const isDone = completed.includes(entity.id);
                            const isCurrent = currentEntity === entity.id;

                            return (
                                <div
                                    key={entity.id}
                                    onClick={() => !isExporting && toggleEntity(entity.id)}
                                    className={`
                                        flex items-center justify-between p-4 rounded-xl border-2 transition-all cursor-pointer
                                        ${isSelected ? 'border-indigo-500 bg-indigo-50' : 'border-gray-100 hover:border-gray-200'}
                                        ${isExporting ? 'opacity-70 cursor-not-allowed' : ''}
                                    `}
                                >
                                    <div className="flex items-center gap-3">
                                        {isSelected ? (
                                            <CheckSquare className="w-5 h-5 text-indigo-600" />
                                        ) : (
                                            <Square className="w-5 h-5 text-gray-300" />
                                        )}
                                        <span className={`font-medium ${isSelected ? 'text-indigo-900' : 'text-gray-700'}`}>
                                            {entity.label}
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {isDone && <CheckCircle className="w-5 h-5 text-green-500" />}
                                        {isCurrent && <Loader2 className="w-5 h-5 text-indigo-600 animate-spin" />}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {isExporting && currentEntity && (
                        <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-100">
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-medium text-gray-700">
                                    {t('export.exporting', { defaultValue: 'Exporting' })} {entities.find(e => e.id === currentEntity)?.label}...
                                </span>
                                <span className="text-sm font-bold text-indigo-600">
                                    {progress ? Math.round((progress.current / progress.total) * 100) : 0}%
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                    className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${progress ? (progress.current / progress.total) * 100 : 0}%` }}
                                ></div>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="mt-4 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        disabled={isExporting}
                        className="px-6 py-2 text-gray-700 font-medium hover:bg-gray-200 rounded-xl transition-colors disabled:opacity-50"
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        onClick={handleStartExport}
                        disabled={isExporting || selectedEntities.length === 0}
                        className="px-6 py-2 bg-indigo-600 text-white font-bold rounded-xl shadow-lg hover:bg-indigo-700 transition-all disabled:opacity-50 disabled:shadow-none flex items-center gap-2"
                    >
                        {isExporting ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                {t('export.processing', { defaultValue: 'Processing...' })}
                            </>
                        ) : (
                            <>
                                <Download className="w-4 h-4" />
                                {t('export.start', { defaultValue: 'Start Export' })}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExportModal;
